
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { settingsService, UserPreferences } from '../../services/settings.mock';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import {
  Lock,
  ShieldCheck,
  Bell,
  Eye,
  Globe,
  Smartphone,
  LogOut,
  CheckCircle2,
  Clock,
  Save,
  Key,
  Mail
} from 'lucide-react';

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState<UserPreferences>(settingsService.getPreferences());
  const [isSaving, setIsSaving] = useState(false);
  const [passForm, setPassForm] = useState({ old: '', new: '', confirm: '' });
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const toggleNotify = (key: keyof UserPreferences['notifications']) => {
    setPrefs(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: !prev.notifications[key] }
    }));
  };

  const handleSavePrefs = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsSaving(false);
    showToast('Platform preferences updated successfully', 'success');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passForm.old || !passForm.new || !passForm.confirm) {
      showToast('Please fill all password fields', 'error');
      return;
    }
    if (passForm.new !== passForm.confirm) {
      showToast('New passwords do not match', 'error');
      return;
    }

    setIsChangingPass(true);
    try {
      const res = await authService.changePassword({
        currentPassword: passForm.old,
        newPassword: passForm.new
      });

      if (res.success) {
        showToast('Access Key rotated successfully', 'success');
        setPassForm({ old: '', new: '', confirm: '' });
      } else {
        showToast(res.message || 'Key rotation failed', 'error');
      }
    } catch (error) {
      showToast('Internal protocol error', 'error');
    } finally {
      setIsChangingPass(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">

      {/* 1. Header with Success Feedback */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase italic">Nexus Configuration</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Platform tuning & account security protocols</p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

        {/* Left Column: Account & Security */}
        <div className="lg:col-span-7 space-y-8">

          {/* Identity Section */}
          <section className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <Mail size={16} className="text-brand-primary" />
              Identity Verification
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-brand-dark/50 border border-brand-border rounded-2xl flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest mb-1">Primary Terminal Email</p>
                  <p className="text-sm font-bold text-white font-mono">{user?.email}</p>
                </div>
                <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase border border-emerald-500/20">Active</span>
              </div>
            </div>
          </section>

          {/* Access Key Rotation (Password Change) */}
          <section className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <Key size={16} className="text-brand-primary" />
              Access Key Rotation
            </h3>
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <Input
                label="Current Terminal Key"
                type="password"
                placeholder="••••••••"
                value={passForm.old}
                onChange={e => setPassForm({ ...passForm, old: e.target.value })}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="New Secure Key"
                  type="password"
                  placeholder="8+ characters"
                  value={passForm.new}
                  onChange={e => setPassForm({ ...passForm, new: e.target.value })}
                />
                <Input
                  label="Confirm Rotation"
                  type="password"
                  placeholder="Repeat key"
                  value={passForm.confirm}
                  onChange={e => setPassForm({ ...passForm, confirm: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full md:w-fit px-12 h-12" variant="secondary" isLoading={isChangingPass}>
                COMMIT KEY CHANGE
              </Button>
            </form>
          </section>

          {/* Security Logs (Placeholder UI) */}
          <section className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <Clock size={16} className="text-brand-primary" />
              Session Audit Trail
            </h3>
            <div className="space-y-3">
              {[
                { action: 'Login Success', ip: '192.168.1.1', date: 'Today, 14:22' },
                { action: 'Withdrawal Initiated', ip: '192.168.1.1', date: 'Yesterday, 09:15' },
                { action: 'Access Key Rotation', ip: '68.12.44.201', date: 'Mar 12, 11:05' },
              ].map((log, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-brand-border last:border-0">
                  <div>
                    <p className="text-[11px] font-bold text-white uppercase tracking-tight">{log.action}</p>
                    <p className="text-[9px] text-brand-muted font-mono">{log.ip}</p>
                  </div>
                  <span className="text-[10px] text-brand-muted font-bold uppercase tracking-widest">{log.date}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column: Preferences & Global Actions */}
        <div className="lg:col-span-5 space-y-8">

          {/* Platform Notifications */}
          <section className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <Bell size={16} className="text-brand-primary" />
              Platform Alerting
            </h3>
            <div className="space-y-6">
              <ToggleItem
                label="Email Notifications"
                active={prefs.notifications.email}
                onToggle={() => toggleNotify('email')}
              />
              <ToggleItem
                label="Push Execution Alerts"
                active={prefs.notifications.push}
                onToggle={() => toggleNotify('push')}
              />
              <ToggleItem
                label="SMS Flash Alerts"
                active={prefs.notifications.sms}
                onToggle={() => toggleNotify('sms')}
              />
              <div className="pt-4">
                <Button onClick={handleSavePrefs} isLoading={isSaving} className="w-full h-12">
                  SAVE ALL PREFERENCES
                </Button>
              </div>
            </div>
          </section>



          {/* Critical Actions */}
          <div className="space-y-4">
            <button
              onClick={logout}
              className="w-full py-4 bg-red-500/5 border border-red-500/20 rounded-2xl flex items-center justify-center gap-3 text-red-500 text-[10px] font-black uppercase tracking-[0.3em] hover:bg-red-500/10 transition-all"
            >
              <LogOut size={16} />
              Terminate All Sessions
            </button>
            <p className="text-center text-[9px] text-brand-muted uppercase tracking-[0.15em] font-bold px-4">
              Warning: This action will force de-authentication across all mobile and terminal instances.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToggleItem: React.FC<{ label: string, active: boolean, onToggle: () => void }> = ({ label, active, onToggle }) => (
  <div className="flex items-center justify-between group cursor-pointer" onClick={onToggle}>
    <span className="text-[11px] font-bold text-brand-muted group-hover:text-white transition-colors uppercase tracking-widest">{label}</span>
    <div className={`w-11 h-6 rounded-full relative transition-all duration-300 ${active ? 'bg-brand-primary' : 'bg-brand-border'}`}>
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-brand-card transition-all duration-300 ${active ? 'left-6' : 'left-1'}`}></div>
    </div>
  </div>
);

export default Settings;
